import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../lib/queryKeys';
import { createContract } from '../api/contracts.js';
import { copyToClipboard } from '../utils.js';
import { useToast } from '../contexts/ToastContext';
import { contractDefaultsFromOfferTerms } from '../lib/contractPrefill.js';
import { CONTRACT_APPS, appFullName } from '../constants/apps.js';

const todayIso = () => {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
};

const parsePastedDate = (raw) => {
  const s = String(raw ?? '').trim();
  if (!s) return null;
  const iso = s.match(/^(\d{4})[-./](\d{1,2})[-./](\d{1,2})$/);
  const dmy = s.match(/^(\d{1,2})[-./](\d{1,2})[-./](\d{4})$/);
  const m = iso ?? dmy;
  if (!m) return null;
  const [y, mo, d] = iso ? [m[1], m[2], m[3]] : [m[3], m[2], m[1]];
  const yy = Number(y), mm = Number(mo), dd = Number(d);
  if (mm < 1 || mm > 12 || dd < 1 || dd > 31) return null;
  return `${y}-${String(mm).padStart(2, '0')}-${String(dd).padStart(2, '0')}`;
};

export default function CreateContractInlineForm({ lead, onCancel, onCreated }) {
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  const [form, setForm] = useState({
    placementType: 'recurring',
    application: CONTRACT_APPS[0],
    hasPrepayment: true,
    prepaymentAmount: '',
    effectiveDate: todayIso(),
    postsPerMonth: 20,
    hasTrialPeriod: true,
    ...contractDefaultsFromOfferTerms(lead),
  });

  const mutation = useMutation({
    mutationFn: createContract,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.contracts.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.contracts.byLead(lead.id) });
      addToast('Создано', 'Контракт создан', 'success');
      if (data.formUrl) {
        copyToClipboard(data.formUrl);
        addToast('Ссылка скопирована', data.formUrl, 'info');
      }
      onCreated?.(data);
    },
    onError: (err) => {
      addToast('Ошибка', err?.response?.data?.error || 'Не удалось создать контракт', 'error');
    },
  });

  const updateField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.effectiveDate) return addToast('Ошибка', 'Укажите дату начала действия', 'error');
    if (!form.ambassadorFee || Number(form.ambassadorFee) <= 0) return addToast('Ошибка', 'Укажите сумму вознаграждения', 'error');
    if (form.placementType === 'recurring') {
      if (!form.incentiveAmount || Number(form.incentiveAmount) <= 0) return addToast('Ошибка', 'Укажите сумму бонуса', 'error');
      if (form.hasTrialPeriod && (!form.trialAmount || Number(form.trialAmount) <= 0)) {
        return addToast('Ошибка', 'Укажите сумму тестового периода', 'error');
      }
    }

    mutation.mutate(form.placementType === 'onetime'
      ? {
          placementType: 'onetime',
          leadId: lead.id,
          effectiveDate: form.effectiveDate,
          application: form.application,
          ambassadorFee: Number(form.ambassadorFee),
          hasPrepayment: form.hasPrepayment,
          ...(form.hasPrepayment && form.prepaymentAmount !== ''
            ? { prepaymentAmount: Number(form.prepaymentAmount) } : {}),
        }
      : {
          placementType: 'recurring',
          leadId: lead.id,
          effectiveDate: form.effectiveDate,
          application: form.application,
          postsPerMonth: Number(form.postsPerMonth),
          hasTrialPeriod: form.hasTrialPeriod,
          trialAmount: form.hasTrialPeriod ? Number(form.trialAmount) : null,
          ambassadorFee: Number(form.ambassadorFee),
          incentiveAmount: Number(form.incentiveAmount),
        });
  };

  return (
    <form className="contract-inline-form" onSubmit={handleSubmit}>
      <div className="contract-form__placement-toggle" style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        {[['recurring', 'Постоянное'], ['onetime', 'Разовое']].map(([val, label]) => (
          <button
            key={val}
            type="button"
            className="documents-action-btn"
            aria-pressed={form.placementType === val}
            style={form.placementType === val
              ? { background: '#7c3aed', color: '#fff', borderColor: '#7c3aed' }
              : undefined}
            onClick={() => setForm((f) => ({ ...f, placementType: val }))}
          >
            {label}
          </button>
        ))}
      </div>
      <div className="contract-inline-form__grid">
        <div className="contract-inline-form__field">
          <label>Дата начала действия</label>
          <input
            type="date"
            value={form.effectiveDate}
            onChange={(e) => updateField('effectiveDate', e.target.value)}
            onPaste={(e) => {
              const parsed = parsePastedDate(e.clipboardData.getData('text'));
              if (parsed) {
                e.preventDefault();
                updateField('effectiveDate', parsed);
              }
            }}
          />
        </div>

        <div className="contract-inline-form__field">
          <label>Приложение</label>
          <select
            value={form.application}
            onChange={(e) => updateField('application', e.target.value)}
          >
            {CONTRACT_APPS.map((a) => (
              <option key={a} value={a}>{appFullName(a)}</option>
            ))}
          </select>
        </div>

        {form.placementType === 'recurring' && (
          <div className="contract-inline-form__field">
            <label>Постов в месяц</label>
            <select
              value={form.postsPerMonth}
              onChange={(e) => updateField('postsPerMonth', e.target.value)}
            >
              <option value={20}>20</option>
              <option value={30}>30</option>
            </select>
          </div>
        )}

        <div className="contract-inline-form__field">
          <label>Вознаграждение ($)</label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={form.ambassadorFee}
            onChange={(e) => updateField('ambassadorFee', e.target.value)}
            placeholder="500"
          />
        </div>

        {form.placementType === 'recurring' && (
          <div className="contract-inline-form__field">
            <label>Бонус ($)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={form.incentiveAmount}
              onChange={(e) => updateField('incentiveAmount', e.target.value)}
              placeholder="250"
            />
          </div>
        )}

        {form.placementType === 'recurring' && (
          <div className="contract-inline-form__field contract-inline-form__field--full">
            <label className="contract-inline-form__checkbox">
              <input
                type="checkbox"
                checked={form.hasTrialPeriod}
                onChange={(e) => updateField('hasTrialPeriod', e.target.checked)}
              />
              <span>Тестовый период</span>
            </label>
          </div>
        )}

        {form.placementType === 'recurring' && form.hasTrialPeriod && (
          <div className="contract-inline-form__field contract-inline-form__field--full">
            <label>Сумма тестового периода ($)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={form.trialAmount}
              onChange={(e) => updateField('trialAmount', e.target.value)}
              placeholder="200"
            />
          </div>
        )}

        {form.placementType === 'onetime' && (
          <div className="contract-inline-form__field contract-inline-form__field--full">
            <label className="contract-inline-form__checkbox">
              <input
                type="checkbox"
                checked={form.hasPrepayment}
                onChange={(e) => setForm((f) => ({ ...f, hasPrepayment: e.target.checked }))}
              />
              <span>Предоплата</span>
            </label>
          </div>
        )}

        {form.placementType === 'onetime' && form.hasPrepayment && (
          <div className="contract-inline-form__field contract-inline-form__field--full">
            <label>Сумма предоплаты ($)</label>
            <input
              type="number" min="0" step="0.01"
              value={form.prepaymentAmount}
              onChange={(e) => setForm((f) => ({ ...f, prepaymentAmount: e.target.value }))}
              placeholder="200"
            />
          </div>
        )}
      </div>

      <div className="contract-inline-form__actions">
        <button
          type="button"
          className="documents-action-btn"
          onClick={onCancel}
          disabled={mutation.isPending}
        >
          Отмена
        </button>
        <button
          type="submit"
          className="documents-action-btn documents-action-btn--primary"
          disabled={mutation.isPending}
        >
          {mutation.isPending ? 'Создание…' : 'Создать'}
        </button>
      </div>
    </form>
  );
}
